import { StudentDecision } from '@/redux/slices/studentDecisionWebsocketSlice'
import { useEffect, useState, useMemo } from 'react'
import OGEAvatar from '../OGE/OGEAvatar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '../ui/tooltip'
import { Button } from '../ui/button'
import {
  EyeIcon,
  PaperAirplaneIcon,
  ClockIcon,
  CheckCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { Card } from '../ui/card'
import { setActiveTab } from '@/redux/slices/chatSlice'
import { ActiveTab } from '@/constants/chat'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import AdjudicationChat from './AdjudicationChat'
import { SERVER_URL } from '@/api/config'
import { Badge } from '../ui/badge'
import { useSelector } from 'react-redux'
import { selectRoomPlayers } from '@/redux/selectors/roomSelector'
import { GroupAdjudicationModal } from './GroupAdjudicationModal'
import { TimerMode } from '@/components/settings/timer-configuration/types'

export enum FilterType {
  ALL = 'all',
  PENDING = 'pending',
  COMPLETED = 'completed',
}

interface GroupedUserDecision {
  decisions: StudentDecision[]
  latestDecision: StudentDecision
  hasPending: boolean
  hasCompleted: boolean
}

function ProfessorAdjudication({
  decisions,
  setSelectedDecision,
  roomId,
}: {
  decisions: StudentDecision[] | null
  setSelectedDecision: (
    decision: StudentDecision,
    studentDecisions: StudentDecision[]
  ) => void
  roomId: string
}) {
  const dispatch = useAppDispatch()
  const roomPlayers = useSelector(selectRoomPlayers)
  const { teams } = useAppSelector((state) => state.team)
  const gameMode = useAppSelector(
    (state) => state.room.currentRoom?.game_mode
  )
  const isResearchMode = gameMode === TimerMode.RESEARCH_MODE

  const [selectedConversation, setSelectedConversation] =
    useState<StudentDecision | null>(null)
  const [filter, setFilter] = useState<FilterType>(FilterType.ALL)
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false)

  useEffect(() => {
    if (selectedConversation) {
      dispatch(setActiveTab(ActiveTab.Adjudication))
    }

    return () => {
      dispatch(setActiveTab(null))
    }
  }, [selectedConversation])

  useEffect(() => {
    if (decisions?.length) {
      setSelectedConversation(decisions[decisions.length - 1])
    }
  }, [decisions])

  const groupedDecisions = useMemo(() => {
    if (!decisions) return []

    const userGroups = new Map<string, GroupedUserDecision>()

    decisions.forEach((decision) => {
      const userId =
        decision.student_id?.toString() || decision.user || 'anonymous'

      if (!userGroups.has(userId)) {
        userGroups.set(userId, {
          decisions: [],
          latestDecision: decision,
          hasPending: false,
          hasCompleted: false,
        })
      }

      const group = userGroups.get(userId)!
      group.decisions.push(decision)

      if (decision.adjudication) {
        group.hasCompleted = true
      } else {
        group.hasPending = true
      }
    })

    return Array.from(userGroups.values()).map((group) => {
      group.decisions.sort(
        (a, b) => (a.round_number || 0) - (b.round_number || 0)
      )

      group.latestDecision = group.decisions[group.decisions.length - 1]

      return group
    })
  }, [decisions])

  const filteredGroups = useMemo(() => {
    const pending = groupedDecisions.filter((group) => group.hasPending)
    const completed = groupedDecisions.filter(
      (group) => !group.hasPending && group.hasCompleted
    )

    switch (filter) {
      case FilterType.PENDING:
        return pending
      case FilterType.COMPLETED:
        return completed
      default:
        return [...pending, ...completed]
    }
  }, [groupedDecisions, filter])

  const getDisplayName = (group: GroupedUserDecision) => {
    const decision = group.latestDecision
    return decision?.user_name?.trim() || decision?.user || 'Anonymous'
  }

  const getUserProfilePicture = (group: GroupedUserDecision) => {
    const decision = group.latestDecision
    return decision?.user_profile_picture
  }

  const getTeamName = (group: GroupedUserDecision) => {
    const player = roomPlayers?.find(
      (p) =>
        p.member_id?.toString() === group.latestDecision.student_id.toString()
    )

    if (!player?.teamId) return null

    const team = teams?.find((t) => t.id === player.teamId)
    return team?.name || null
  }

  const getStatusInfo = (group: GroupedUserDecision) => {
    const hasLateSubmission = group.decisions.some((d) => d.late_submission)

    if (!group.hasPending && group.hasCompleted) {
      return {
        status: FilterType.COMPLETED,
        color: 'text-green-600',
        bgColor: 'bg-green-50 border-green-200',
        icon: CheckCircleIcon,
        badgeVariant: 'default' as const,
        badgeColor: 'bg-green-100 text-green-800 border-green-300',
      }
    } else {
      return {
        status: FilterType.PENDING,
        color: hasLateSubmission ? 'text-red-600' : 'text-orange-600',
        bgColor: hasLateSubmission
          ? 'bg-red-50 border-red-200'
          : 'bg-orange-50 border-orange-200',
        icon: ClockIcon,
        badgeVariant: 'destructive' as const,
        badgeColor: hasLateSubmission
          ? 'bg-red-100 text-red-800 border-red-300'
          : 'bg-orange-100 text-orange-800 border-orange-300',
      }
    }
  }

  const pendingCount = useMemo(
    () => groupedDecisions.filter((group) => group.hasPending).length,
    [groupedDecisions]
  )

  const completedCount = useMemo(
    () =>
      groupedDecisions.filter(
        (group) => !group.hasPending && group.hasCompleted
      ).length,
    [groupedDecisions]
  )

  const handleGroupAdjudication = () => {
    setIsGroupModalOpen(true)
  }

  return (
    <div className="overflow-auto flex-1 min-w-0 max-w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none'] mt-0">
      <Card className='flex min-w-0 max-w-full'>
        <div className='h-[calc(100vh-220px)] min-w-[320px] overflow-auto p-3 border-r bg-gray-50/30'>
          <div className='flex items-center justify-between mb-4'>
            <div className='flex items-center gap-2'>
              <strong className='text-lg'>Decisions</strong>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={handleGroupAdjudication}
                    className='flex items-center gap-2 border-sky-300 bg-sky-50 hover:bg-sky-100 text-sky-700'
                  >
                    <UserGroupIcon className='w-4 h-4' />
                    Group Adjudication
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Adjudicate multiple decisions at once
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className='flex gap-1 mb-4 p-1 bg-gray-100 rounded-lg'>
            {[
              {
                key: FilterType.ALL,
                label: 'All',
                count: filteredGroups.length,
              },
              {
                key: FilterType.PENDING,
                label: 'Pending',
                count: pendingCount,
              },
              {
                key: FilterType.COMPLETED,
                label: 'Completed',
                count: completedCount,
              },
            ].map(({ key, label, count }) => (
              <Button
                key={key}
                variant={filter === key ? 'default' : 'ghost'}
                size='sm'
                onClick={() => setFilter(key as typeof filter)}
                className={`flex-1 justify-center whitespace-nowrap ${
                  filter === key
                    ? 'bg-white text-dark-blue shadow-sm border border-sky-200 hover:bg-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {label}
                {count > 0 && (
                  <Badge
                    variant={filter === key ? 'secondary' : 'outline'}
                    className={`h-5 px-1.5 text-xs ${
                      filter === key
                        ? 'bg-sky-100 text-dark-blue border-sky-200'
                        : 'bg-gray-200 text-gray-600 border-gray-300'
                    }`}
                  >
                    {count}
                  </Badge>
                )}
              </Button>
            ))}
          </div>

          <div className='space-y-2'>
            {filteredGroups.map((group) => {
              const displayName = getDisplayName(group)
              const userProfilePicture = getUserProfilePicture(group)
              const teamName = getTeamName(group)
              const statusInfo = getStatusInfo(group)
              const StatusIcon = statusInfo.icon
              const hasLateSubmission = group.decisions.some(
                (d) => d.late_submission
              )

              return (
                <div
                  key={group.latestDecision.student_id}
                  onClick={() => {
                    setSelectedConversation(group.latestDecision)
                  }}
                  className={`cursor-pointer rounded-lg border-2 transition-all ${
                    selectedConversation?.student_id ===
                    group.latestDecision.student_id
                      ? 'border-orange-300 bg-orange-50 shadow-md'
                      : 'border-transparent bg-white hover:shadow-sm hover:border-gray-300'
                  }`}
                >
                  <div className='p-3'>
                    <div
                      className={`flex items-center gap-2 mb-2 pb-2 border-b border-gray-200`}
                    >
                      <StatusIcon className={`w-4 h-4 ${statusInfo.color}`} />
                      <span
                        className={`text-xs font-semibold uppercase tracking-wide ${statusInfo.color}`}
                      >
                        {statusInfo.status}
                      </span>
                      {hasLateSubmission && (
                        <Badge
                          variant='destructive'
                          className='text-xs ml-auto'
                        >
                          Late
                        </Badge>
                      )}
                      <Badge variant='outline' className='text-xs ml-auto'>
                        {group.decisions.length}
                      </Badge>
                    </div>

                    <div className='flex justify-between items-center'>
                      <div className='flex items-center min-w-0 flex-1 mr-3'>
                        <OGEAvatar
                          src={
                            userProfilePicture
                              ? SERVER_URL + userProfilePicture
                              : null
                          }
                          fallback={displayName.substring(0, 2).toUpperCase()}
                          variant='md'
                          isLoading={false}
                        />
                        <div className='flex flex-col min-w-0 pl-3'>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className='truncate max-w-[140px] font-semibold text-gray-900'>
                                  {displayName}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>{displayName}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          {teamName && (
                            <div className='text-xs text-gray-500 truncate max-w-[120px]'>
                              {teamName}
                            </div>
                          )}
                        </div>
                      </div>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size='sm'
                              variant={
                                !group.hasPending ? 'outline' : 'default'
                              }
                              className={`rounded-full p-2 transition-all ${
                                !group.hasPending
                                  ? 'border-green-300 bg-green-50 hover:bg-green-100 text-green-700'
                                  : hasLateSubmission
                                    ? 'bg-red-600 hover:bg-red-700 text-white'
                                    : 'bg-orange-600 hover:bg-orange-700 text-white'
                              }`}
                              onClick={(event) => {
                                event.stopPropagation()
                                setSelectedDecision(
                                  group.latestDecision,
                                  group.decisions
                                )
                              }}
                            >
                              {!group.hasPending ? (
                                <EyeIcon className='w-4 h-4' />
                              ) : (
                                <PaperAirplaneIcon className='w-4 h-4' />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {!group.hasPending
                              ? 'View Adjudication'
                              : 'Send Adjudication'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {filteredGroups.length === 0 && (
            <div className='flex flex-col items-center justify-center mt-12 p-6'>
              <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4'>
                {filter === FilterType.PENDING ? (
                  <ClockIcon className='w-8 h-8 text-gray-400' />
                ) : filter === FilterType.COMPLETED ? (
                  <CheckCircleIcon className='w-8 h-8 text-gray-400' />
                ) : (
                  <PaperAirplaneIcon className='w-8 h-8 text-gray-400' />
                )}
              </div>
              <p className='text-sm font-medium text-gray-500 text-center'>
                {filter === FilterType.PENDING
                  ? 'No decisions pending adjudication'
                  : filter === FilterType.COMPLETED
                    ? 'No decisions have been adjudicated yet'
                    : 'No decisions submitted yet'}
              </p>
              {filter === FilterType.PENDING &&
                decisions &&
                decisions.length > 0 && (
                  <p className='text-xs text-gray-400 mt-1'>
                    All decisions have been adjudicated
                  </p>
                )}
            </div>
          )}
        </div>
        <div className='flex-1 min-w-0 overflow-hidden'>
          <AdjudicationChat selectedConversation={selectedConversation} />
        </div>
      </Card>

      <GroupAdjudicationModal
        isGroupModalOpen={isGroupModalOpen}
        setIsGroupModalOpen={setIsGroupModalOpen}
        roomId={roomId}
        isResearchMode={isResearchMode}
      />
    </div>
  )
}

export default ProfessorAdjudication
